import { isMemberLoggedIn, useMemberId } from '@trello/authentication';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';
import { usePathname } from '@trello/router';
import { navigate } from '@trello/router/navigate';
import { isMeAlias } from '@trello/urls';

import { useMemberLabsAuthorizationQuery } from './MemberLabsAuthorizationQuery.generated';

type Response = { loading: false; canView: boolean } | { loading: true };

function response(canView: boolean): Response {
  return { loading: false, canView };
}

export const useMemberLabsPageAuthorization = (): Response => {
  const memberId = useMemberId();
  const pathname = usePathname();

  const { data, error, loading } = useMemberLabsAuthorizationQuery({
    ...(isMemberLoggedIn()
      ? {
          variables: {
            memberId,
          },
        }
      : { skip: true }),
    waitOn: ['MemberHeader'],
  });

  if (loading) {
    return { loading: true };
  }

  if (!isMemberLoggedIn() || error) {
    return response(false);
  }

  const username = data?.member?.username;

  if (!username) {
    // prevent labs page from rendering for unauthenticated users
    return response(false);
  }

  const match = pathname.match(/\/?(u)?\/([^/]+)\/?([^/]+)?/);
  if (!match) {
    return response(false);
  }

  const routeUsername = match[2];

  // Set the location to /u/{username}/labs in case the user has navigated
  // via an `isMeAlias` route shortcut (eg, `/my/labs`).
  if (isMeAlias(routeUsername) || match[1] !== 'u') {
    navigate(`/u/${dangerouslyConvertPrivacyString(username)}/labs`, {
      replace: true,
    });
    return response(true);
  }

  // prevent other people from seeing your labs page
  return response(dangerouslyConvertPrivacyString(username) === routeUsername);
};
